import { computed, effect, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';
import * as turf from '@turf/turf';
import { bbox } from '@turf/turf';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import RBush from 'rbush';
import { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import { isEqual } from 'lodash';
import proj4 from 'proj4';
import {
  FarmDossierFabbricatoResponse,
  FarmDossierFascicoloResponse,
  FarmDossierMacchinaResponse,
  FarmDossierParticellaResponse,
  FarmDossierTerritorioResponse,
  FarmDossierZootecniaResponse,
} from '../_models/farmDossier';
import { environment } from '../../../environments/environment';
import {
  DOSSIER_COLUMN_CONFIG,
  EQUIPMENT_COLUMN_CONFIG,
  FABBRICATO_COLUMN_CONFIG,
  HubfarmCultivation,
  HubfarmFeature,
  HubfarmFeatureCollection,
  HubfarmMatchFieldCrops,
  HubfarmMatchFieldFeature,
  HubfarmProperties,
  MatchedHubfarmCultivationData,
  OWNERS_COLUMN_CONFIG,
  PARTICELLA_COLUMN_CONFIG,
  TERRITORY_COLUMN_CONFIG,
  ZOOTECHNIC_COLUMN_CONFIG,
} from '../_models/hubfarm';
import { Commodity, Crop, Field, Plant, Variety } from '../_models';
import { FieldService } from './field.service';
import { CropService } from './crop.service';
import {
  CommonCommodity,
  CommonIntendedUse,
  CommonQuality,
  CommonUse,
  CommonVariety,
} from '../_models/commonService';
import { CommonService } from './common.service';
import { ExternalResource } from '../_models/externalResource';
import { CommodityService } from './commodity.service';
import { TenantMetaData } from '../_models/tenantGeneralData';
import { XYearService } from './xYear.service';
import { MapService } from './map.service';

// Define the structure stored in the RBush index
export type BBoxItem = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  field: Field;
};

@Injectable()
export class FarmDossierService {
  private farmDossierServiceUrl = environment.dossierServiceUrl;

  public $plans: BehaviorSubject<Array<HubfarmFeature>> = new BehaviorSubject<
    Array<HubfarmFeature>
  >(null);

  public $plansToImport: BehaviorSubject<Array<HubfarmFeature>> = new BehaviorSubject<
    Array<HubfarmFeature>
  >([]);
  public $selectedPlansToImport: BehaviorSubject<Array<HubfarmFeature>> = new BehaviorSubject<
    Array<HubfarmFeature>
  >(null);

  public $plansToMatch: BehaviorSubject<Array<HubfarmMatchFieldFeature>> = new BehaviorSubject<
    Array<HubfarmMatchFieldFeature>
  >([]);

  public $selectedPlansToMatch: BehaviorSubject<Array<HubfarmMatchFieldFeature>> =
    new BehaviorSubject<Array<HubfarmMatchFieldFeature>>(null);

  public $cropsToMatch: BehaviorSubject<Array<HubfarmMatchFieldCrops>> = new BehaviorSubject<
    Array<HubfarmMatchFieldCrops>
  >([]);

  public $selectedCropsToMatch: BehaviorSubject<Array<HubfarmMatchFieldCrops>> =
    new BehaviorSubject<Array<HubfarmMatchFieldCrops>>(null);

  private fields = signal<Field[]>(null);
  private crops: Crop[] = null;

  $fileToImport: BehaviorSubject<HubfarmFeatureCollection> =
    new BehaviorSubject<HubfarmFeatureCollection>(null);

  // Convert BehaviorSubjects to signals for reactive computed dependencies
  private plansToImportSignal = toSignal(this.$plansToImport, { initialValue: [] });
  private plansToMatchSignal = toSignal(this.$plansToMatch, { initialValue: [] });
  private cropsToMatchSignal = toSignal(this.$cropsToMatch, { initialValue: [] });

  // selected matches ids
  public selectedFieldMatches = signal<HubfarmMatchFieldFeature[]>([]);

  // signal with just an array of ids for quick template state checks
  public selectedFieldMatchesIds: Signal<number[]> = computed(() =>
    this.selectedFieldMatches().map((p) => p.feature.properties.clientUniqueId)
  );

  // current plan section
  public currentPlanSection = signal<string>('');

  // selected plans ids
  public selectedFeatures = signal<HubfarmFeature[]>([]);

  // signal with just an array of ids for quick template state checks
  public selectedFeaturesIds: Signal<number[]> = computed(() =>
    this.selectedFeatures().map((p) => p.properties.clientUniqueId)
  );

  // selected plans ids
  public selectedCropMatches = signal<HubfarmMatchFieldCrops[]>([]);

  // signal with just an array of ids for quick template state checks
  public selectedCropMatchesIds = computed(() => this.selectedCropMatches().map((m) => m.id));

  // Fields to be displayed on the map, filtered by spatial proximity
  public featuresMapFields = computed((): Field[] => {
    // Get selected features, matches, and crops (using signals for reactivity)
    const features = this.plansToImportSignal();
    const matches = this.plansToMatchSignal();
    const crops = this.cropsToMatchSignal();

    // Initialize spatial index
    const spatialIndex = new RBush<BBoxItem>();

    // Map to store unique fields to avoid duplicates
    const fieldMap = new Map<number, Field>();

    // Index all user fields in the spatial index
    this.fields()?.forEach((field) => {
      // Extract the bounding box from the field's polygon
      const [minX, minY, maxX, maxY] = bbox({
        type: 'Polygon',
        coordinates: field.polygon.coordinates,
      });

      // Insert field into RBush with its bounding box
      spatialIndex.insert({ minX, minY, maxX, maxY, field });
    });

    // Create a minimal Field object from a HubfarmFeature
    const createFieldFromFeature = (feature: HubfarmFeature): Field => {
      // Create new Field instance
      const fakeField = new Field();

      // Set the ID from the feature properties
      fakeField.id = feature.properties.clientUniqueId;

      // Set the polygon using the feature's geometry
      fakeField.polygon = { coordinates: feature.geometry.coordinates };

      // Build a minimal geoJsonArea to render the field
      fakeField.geoJsonArea = JSON.stringify({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: feature.geometry,
          },
        ],
      });

      return fakeField;
    };

    if (this.currentPlanSection() === 'create') {
      // Process all selected features
      features.forEach((feature) => {
        // Create and store the corresponding fake field
        const field = createFieldFromFeature(feature);
        fieldMap.set(field.id, field);

        // Add nearby real fields to the result
        // addNearbyFields(feature.geometry);
      });
    }

    if (this.currentPlanSection() === 'match') {
      // Process all selected matches
      matches.forEach((match) => {
        // Create and store the corresponding fake field
        const field = createFieldFromFeature(match.feature);
        fieldMap.set(field.id, field);

        // Add nearby real fields to the result
        // addNearbyFields(match.feature.geometry);
      });
    }

    if (this.currentPlanSection() === 'import-crops') {
      // Process all selected crop matches
      crops.forEach((cropMatch) => {
        // Add the crop field directly
        fieldMap.set(cropMatch.field.id, cropMatch.field);

        // Add nearby fields around the crop's polygon
        // addNearbyFields(cropMatch.field.polygon as GeoJSON.Polygon);
      });
    }

    // Return unique fields as array
    return Array.from(fieldMap.values());
  });

  // Map color for selected features to import
  featuresMapColors = computed<Map<number, string>>(() => {
    const colorMap = new Map<number, string>();

    // Setting xfarm fields first
    this.fields()?.forEach((field) => {
      colorMap.set(field.id, 'rgb(53, 162, 53)');
    });

    // getting data for color mapping
    const selectedFeatures = this.selectedFeatures() || [];
    const selectedMatches = this.selectedFieldMatches() || [];
    const selectedCrops = this.selectedCropMatches() || [];

    // setting color for selected features
    if (this.currentPlanSection() === 'create') {
      selectedFeatures.forEach((feature: HubfarmFeature): void => {
        colorMap.set(feature.properties.clientUniqueId, 'rgb(0, 123, 255)');
      });
      this.plansToImportSignal().forEach((feature: HubfarmFeature): void => {
        if (!this.selectedFeaturesIds().includes(feature.properties.clientUniqueId)) {
          colorMap.set(feature.properties.clientUniqueId, 'rgb(255,255,255)');
        }
      });
    }

    // setting color for selected xfarm fields
    if (this.currentPlanSection() === 'match') {
      selectedMatches.forEach((match: HubfarmMatchFieldFeature): void => {
        colorMap.set(match.feature.properties.clientUniqueId, 'rgb(0, 123, 255)');
      });
      this.plansToMatchSignal().forEach((match: HubfarmMatchFieldFeature): void => {
        if (!this.selectedFieldMatchesIds().includes(match.feature.properties.clientUniqueId)) {
          colorMap.set(match.feature.properties.clientUniqueId, 'rgb(255,255,255)');
        }
      });
    }

    // setting color for selected crops
    if (this.currentPlanSection() === 'import-crops') {
      selectedCrops.forEach((cropMatch: HubfarmMatchFieldCrops): void => {
        colorMap.set(cropMatch.field.id, 'rgb(0, 123, 255)');
      });
      this.cropsToMatchSignal().forEach((cropMatch: HubfarmMatchFieldCrops): void => {
        if (!this.selectedCropMatchesIds().includes(cropMatch.id)) {
          colorMap.set(cropMatch.field.id, 'rgb(255,255,255)');
        }
      });
    }

    return colorMap;
  });

  commodities: Commodity[];
  varieties: Variety[];

  commonCommodities: CommonCommodity[];
  commonVarieties: CommonVariety[];
  commonIntendedUses: CommonIntendedUse[];
  commonQualities: CommonQuality[];
  commonUses: CommonUse[];

  private lastSyncedFields: Field[] = null;
  private lastSyncedCrops: Crop[] = null;

  constructor(
    private http: HttpClient,
    private fieldService: FieldService,
    private cropService: CropService,
    private commonService: CommonService,
    private commodityService: CommodityService,
    private xYearService: XYearService,
    private mapService: MapService
  ) {
    effect(() => {
      const fields = this.featuresMapFields();

      if (!fields.length) {
        this.mapService.customBounds$.next(null);
      }

      const bounds = new google.maps.LatLngBounds();
      fields.forEach((field) => {
        if (field.polygon && field.polygon.coordinates) {
          const coords = field.polygon.coordinates[0].map((coord) => ({
            lat: coord[1],
            lng: coord[0],
          }));
          coords.forEach((latlng) => bounds.extend(latlng));
        }
      });
      this.mapService.customBounds$.next(bounds);
    });
  }

  // Gets all the data required from xfarm and Common to match hubfarm entities to xfarm entitites
  bootstrapMatchingData() {
    return new Observable((observer) => {
      // getting the just to get current year xfarm fields
      firstValueFrom(this.xYearService.selectedYear$)
        .then(() => {
          return Promise.all([
            firstValueFrom(this.fieldService.getFields(null, null, null, null, null, false)),
            firstValueFrom(this.cropService.getCrops()),
            firstValueFrom(this.commodityService.getFromLocalStore()),
            firstValueFrom(this.cropService.getVarieties()),
            firstValueFrom(this.commonService.getCommodities(null, null, null, 'AGEA')),
            firstValueFrom(this.commonService.getVarieties(null, null, null, 'AGEA')),
            firstValueFrom(this.commonService.getIntendedUses(null, null, null, 'AGEA')),
            firstValueFrom(this.commonService.getQualities(null, null, null, 'AGEA')),
            firstValueFrom(this.commonService.getUses(null, null, null, 'AGEA')),
          ]);
        })
        .then(
          ([
            fields,
            crops,
            commodities,
            varieties,
            commonCommodities,
            commonVarieties,
            commonIntendedUses,
            commonQualities,
            commonUses,
          ]) => {
            this.fields.set(fields);
            this.crops = crops;
            this.commodities = commodities;
            this.varieties = varieties;
            this.commonCommodities = commonCommodities;
            this.commonVarieties = commonVarieties;
            this.commonIntendedUses = commonIntendedUses;
            this.commonQualities = commonQualities;
            this.commonUses = commonUses;
            observer.next(true);
            observer.complete();
          }
        )
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  // Returns a service from a given keyword
  getDataByType(
    type: string,
    cuaa: string,
    date?: string
  ): Observable<
    | FarmDossierFascicoloResponse
    | FarmDossierMacchinaResponse
    | FarmDossierFabbricatoResponse
    | FarmDossierTerritorioResponse
    | FarmDossierZootecniaResponse
  > {
    // the hubfarm API does not send the latest data vailable
    // passing today's date, so when the date is today we pass null
    if (moment(date).isSame(moment(), 'day')) {
      date = null;
    }

    if (type == 'dossier') {
      return this.getDossier(cuaa, date);
    }
    if (type == 'equipments') {
      return this.getEquipments(cuaa, date);
    }
    if (type == 'particelle') {
      return this.getParticles(cuaa, date);
    }
    if (type == 'territory') {
      return this.getTerritories(cuaa, date);
    }
    if (type == 'zootechnic') {
      return this.getZootechnics(cuaa, date);
    }
    if (type == 'building') {
      return this.getBuildings(cuaa, date);
    }
    return undefined;
  }

  getDossier(cuaa: string, date?: string): Observable<FarmDossierFascicoloResponse> {
    return this.http
      .get<any>(`${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}`, {
        params: {
          // all = true returns all elements without pagination,
          // for now we keep it like this or the search filter on the table won't work
          all: true,
          snapshotDate: date,
        },
      })
      .pipe(
        map((res) => {
          return {
            items: [res],
          };
        })
      );
  }

  getEquipments(cuaa: string, date?: string): Observable<FarmDossierMacchinaResponse> {
    return this.http
      .get<FarmDossierMacchinaResponse>(
        `${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}/macchina`,
        {
          params: {
            // all = true returns all elements without pagination,
            // for now we keep it like this or the search filter on the table won't work
            all: true,
            snapshotDate: date,
          },
        }
      )
      .pipe(map((res) => res));
  }

  getParticles(cuaa: string, date?: string): Observable<FarmDossierParticellaResponse> {
    return this.http
      .get<FarmDossierParticellaResponse>(
        `${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}/particella`,
        {
          params: {
            // all = true returns all elements without pagination,
            // for now we keep it like this or the search filter on the table won't work
            all: true,
            snapshotDate: date,
          },
        }
      )
      .pipe(map((res) => res));
  }

  getBuildings(cuaa: string, date?: string): Observable<FarmDossierFabbricatoResponse> {
    return this.http
      .get<FarmDossierFabbricatoResponse>(
        `${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}/fabbricato`,
        {
          params: {
            // all = true returns all elements without pagination,
            // for now we keep it like this or the search filter on the table won't work
            all: true,
            snapshotDate: date,
          },
        }
      )
      .pipe(map((res) => res));
  }

  getTerritories(cuaa: string, date?: string): Observable<FarmDossierTerritorioResponse> {
    return this.http
      .get<FarmDossierTerritorioResponse>(
        `${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}/territorio`,
        {
          params: {
            // all = true returns all elements without pagination,
            // for now we keep it like this or the search filter on the table won't work
            all: true,
            snapshotDate: date,
          },
        }
      )
      .pipe(map((res) => res));
  }

  getZootechnics(cuaa: string, date?: string): Observable<FarmDossierZootecniaResponse> {
    return this.http
      .get<FarmDossierZootecniaResponse>(
        `${this.farmDossierServiceUrl}/hubfarm/fascicolo/${cuaa}/zootecnia`,
        {
          params: {
            // all = true returns all elements without pagination,
            // for now we keep it like this or the search filter on the table won't work
            all: true,
            snapshotDate: date,
          },
        }
      )
      .pipe(map((res) => res));
  }

  /**
   * Fetches the list of plans available for the current farm from the Hubfarm API
   * @param cuaa
   * @returns Observable<Array<HubfarmFeature>>
   */
  getPlans(cuaa: string, file?: HubfarmFeatureCollection): Observable<HubfarmFeatureCollection> {
    // getting new plans requires resetting selectedITems, but we want to keep selected those that still exist

    if (!file) {
      return this.http
        .get(`${this.farmDossierServiceUrl}/hubfarm/pianodicoltivazione/${cuaa}`)
        .pipe(
          map((res: HubfarmFeatureCollection) => {
            // Parsing all data for plan import module use
            // If response is empty object or invalid featureCollection return
            if (res.features && res.features.length) {
              this.parsePlanData(res);
            }
            return res;
          }),
          catchError((err) => {
            console.error('Error fetching plans', err);
            // Parsing all data for plan import module use
            return throwError(() => err);
          })
        );
    } else {
      // If file is passed, we parse it directly
      return new Observable<HubfarmFeatureCollection>((observer) => {
        this.parsePlanData(file);
        observer.next(file);
        observer.complete();
      });
    }
  }

  isLikelyWGS84(positions: any): boolean {
    const flatten = (coords: any): [number, number][] => {
      if (typeof coords[0] === 'number') {
        // Caso Point
        return [[coords[0], coords[1]]];
      } else if (typeof coords[0][0] === 'number') {
        // Caso LineString
        return coords as [number, number][];
      } else {
        // Caso Polygon o MultiPolygon → ricorsione
        return coords.flatMap(flatten);
      }
    };

    const coords = flatten(positions);

    return coords.every(([lon, lat]) => lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90);
  }

  /**
   * Detects the EPSG code from feature properties
   * @param feature - The feature to analyze
   * @returns The detected EPSG code or null if not found
   */
  detectEPSGFromFeature(
    feature: Feature<Polygon | MultiPolygon, HubfarmProperties>
  ): string | null {
    if (!feature?.properties) return null;

    const props = feature.properties;

    // Priority order for EPSG detection
    const epsgFields = [
      'epsg3263x', // Your specific field
      'epsg1', // Your specific field
      'epsg', // Standard field
      'codiEpsg', // Alternative field
    ];

    for (const field of epsgFields) {
      if (props[field] && props[field].toString().trim() !== '') {
        const epsgCode = props[field].toString().trim();
        // Validate that it's a valid EPSG code
        if (/^\d+$/.test(epsgCode)) {
          return epsgCode;
        }
      }
    }

    return null;
  }

  /**
   * Gets the proj4 string for a given EPSG code
   * @param epsgCode - The EPSG code
   * @returns The proj4 string or null if not supported
   */
  getProj4String(epsgCode: string): string | null {
    const projections: { [key: string]: string } = {
      // UTM Zones (WGS84)
      '32632': '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
      '32633': '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs',
      '32634': '+proj=utm +zone=34 +datum=WGS84 +units=m +no_defs',
      '32635': '+proj=utm +zone=35 +datum=WGS84 +units=m +no_defs',

      // UTM Zones (ETRS89)
      '25832': '+proj=utm +zone=32 +datum=ETRS89 +units=m +no_defs',
      '25833': '+proj=utm +zone=33 +datum=ETRS89 +units=m +no_defs',
      '25834': '+proj=utm +zone=34 +datum=ETRS89 +units=m +no_defs',

      // Italian systems
      '3003':
        '+proj=tmerc +lat_0=0 +lon_0=9 +k=0.9996 +x_0=1500000 +y_0=0 +ellps=intl +towgs84=-225,-65,9,0,0,0,0 +units=m +no_defs', // Monte Mario
      '3004':
        '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.9996 +x_0=2520000 +y_0=0 +ellps=intl +towgs84=-225,-65,9,0,0,0,0 +units=m +no_defs', // Monte Mario (Italy 2)

      // Web Mercator
      '3857':
        '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs',

      // WGS84 (already in correct format)
      '4326': '+proj=longlat +datum=WGS84 +no_defs',
    };

    return projections[epsgCode] || null;
  }

  /**
   * Converts a polygon from any supported EPSG to WGS84 (EPSG:4326)
   * @returns Array of coordinates [[lon, lat], [lon, lat], ...] in WGS84
   * @param feature
   */
  convertFeatureToWGS84(
    feature: Feature<Polygon | MultiPolygon, HubfarmProperties>
  ): Feature<Polygon | MultiPolygon, HubfarmProperties> {
    if (!feature || !feature.geometry || !feature.geometry.coordinates) return feature;

    // Try to detect EPSG from feature properties
    const detectedEPSG = this.detectEPSGFromFeature(feature);

    if (!detectedEPSG) {
      console.warn('No EPSG code detected, using default UTM Zone 33N (EPSG:32633)');
      // Fallback to UTM Zone 33N as default
      const utmZone33N = '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs';
      const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';

      const convertCoord = (coord: Position): [number, number] => {
        const [x, y] = coord;
        return proj4(utmZone33N, wgs84, [x, y]);
      };

      return this.convertGeometry(feature, convertCoord);
    }

    // Get the proj4 string for the detected EPSG
    const sourceProj = this.getProj4String(detectedEPSG);

    if (!sourceProj) {
      console.warn(`Unsupported EPSG code: ${detectedEPSG}, using default UTM Zone 33N`);
      // Fallback to UTM Zone 33N
      const utmZone33N = '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs';
      const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';

      const convertCoord = (coord: Position): [number, number] => {
        const [x, y] = coord;
        return proj4(utmZone33N, wgs84, [x, y]);
      };

      return this.convertGeometry(feature, convertCoord);
    }

    const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';

    // Function to convert a single coordinate
    const convertCoord = (coord: Position): [number, number] => {
      const [x, y] = coord;
      return proj4(sourceProj, wgs84, [x, y]);
    };

    return this.convertGeometry(feature, convertCoord);
  }

  /**
   * Helper function to convert geometry based on a coordinate conversion function
   * @param feature - The feature to convert
   * @param convertCoord - Function to convert a single coordinate
   * @returns The converted feature
   */
  private convertGeometry(
    feature: Feature<Polygon | MultiPolygon, HubfarmProperties>,
    convertCoord: (coord: Position) => [number, number]
  ): Feature<Polygon | MultiPolygon, HubfarmProperties> {
    // Convert based on geometry type
    if (feature.geometry.type === 'Polygon') {
      const polygonCoords = feature.geometry.coordinates as Position[][];
      const converted: Polygon = {
        type: 'Polygon',
        coordinates: polygonCoords.map((ring) => ring.map(convertCoord)) as Position[][],
      };
      return {
        ...feature,
        geometry: converted,
      };
    } else {
      const multiPolygonCoords = feature.geometry.coordinates as Position[][][];
      const converted: MultiPolygon = {
        type: 'MultiPolygon',
        coordinates: multiPolygonCoords.map((polygon) =>
          polygon.map((ring) => ring.map(convertCoord))
        ) as Position[][][],
      };
      return {
        ...feature,
        geometry: converted,
      };
    }
  }

  /**
   * Parses a GeoJSON file to import it into the service.
   * @param file - The GeoJSON file to parse.
   */
  async parseGeoJsonToImport(file: File): Promise<HubfarmFeatureCollection> {
    this.$fileToImport.next(null);
    // Validate the geoJson file
    if (
      !file?.name?.toLowerCase().endsWith('.geojson') &&
      !file?.name?.toLowerCase().endsWith('.json')
    ) {
      throw new Error('Invalid file format. Please upload a .geojson file.');
    }

    // Read the file content
    const reader = new FileReader();
    const content = await new Promise<string>((resolve, reject) => {
      reader.onload = (e) => resolve(e.target.result as string);
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });

    try {
      const jsonContent: HubfarmFeatureCollection = JSON.parse(content);
      // Store the file content in the service

      // Check if is  WSG84
      if (!this.isLikelyWGS84(jsonContent.features[0].geometry.coordinates[0])) {
        // convert to WGS84
        jsonContent.features = jsonContent.features.map((feature) =>
          this.convertFeatureToWGS84(feature)
        );
      }

      this.$fileToImport.next(jsonContent);
      return jsonContent;
    } catch {
      throw new Error('Error parsing JSON file');
    }
  }

  parsePlanData(res: HubfarmFeatureCollection): void {
    // Reset selected items
    this.resetSelectedItems();

    // remove invalid features
    res.features = this.filterValidFeatures(res.features);

    // Returns a unique numeric id
    const getUniqueId = (feature: HubfarmFeature, idx) => {
      // Create a seed based on the feature ID
      const seed = parseInt(feature.properties.id) % 1000;
      // Use the seed to create a unique ID with pseudo-randomness
      return parseInt(feature.properties.id) * 10000 + idx * 10 + (seed % 10);
    };

    // Flatten MultiPolygon into multiple Polygons with group.id
    const flattened: HubfarmFeature[] = [];
    res.features.forEach((feature) => {
      if (feature.geometry.type === 'MultiPolygon') {
        const multiPoly = feature.geometry.coordinates as number[][][][];
        multiPoly.forEach((polygonCoords, idx) => {
          // spread cultivation size along all the polygons
          const cultivations = feature.properties?.coltivazioni?.map((cult) => {
            const cultSizePerPoly = cult.supeColt / multiPoly.length;
            return {
              ...cult,
              supeColt: cultSizePerPoly,
            };
          });

          // outer ring
          const outerRing = polygonCoords[0];
          // clone feature
          const sub: HubfarmFeature = {
            ...feature,
            geometry: {
              type: 'Polygon',
              coordinates: [outerRing],
            },
            properties: {
              ...feature.properties,
              groupId: feature.properties.id,
              clientUniqueId: getUniqueId(feature, idx),
              coltivazioni: cultivations,
            },
          };

          flattened.push(sub);
        });
      } else {
        // Setting unique id also for Polygon
        // tyoes for further logic uniformitt down the line
        feature.properties.clientUniqueId = getUniqueId(feature, 0);
        flattened.push(feature);
      }
    });
    res.features = flattened;

    // emit raw plans stream
    this.$plans.next(res.features);

    // build a set of imported ids to exclude them
    const clientUniqueIdsSet = new Set(
      (this.fields() || []).flatMap((field) => {
        // if there is no metadata, we check for external resources
        if (field.metaData?.length) {
          return (
            field.metaData
              .filter((meta) => meta.key === 'clientUniqueId')
              .map((meta) => meta.value) || []
          );
        }
        return [];
      })
    );
    // To mantain legacy compatibility we also have to check for
    // external resource if there is no metadata present
    // build a set of imported ids to exclude them
    const ExternalIdSet = new Set(
      (this.fields() || []).flatMap((item) => {
        if (!item.externalResources) {
          return [];
        }
        return item.externalResources
          .filter((r) => r.externalSource === 'AGEA')
          .map((r) => r.externalId);
      })
    );

    const _featuresToImport: HubfarmFeature[] = [];
    const _featuresToMatch: HubfarmMatchFieldFeature[] = [];
    const _cropsToMatch: HubfarmMatchFieldCrops[] = [];

    // 2) process each as Polygon
    res.features.forEach((feature) => {
      // skip already imported

      if (clientUniqueIdsSet.has(feature.properties.clientUniqueId.toString())) {
        return;
      }

      if (ExternalIdSet.has(feature.properties.clientUniqueId.toString())) {
        return;
      }

      // if there are no fields, import all
      if (!this.fields()?.length) {
        _featuresToImport.push(feature);
        return;
      }

      // geographic matching
      let matched = false;
      for (const field of this.fields() || []) {
        // Ensuring the right type is passed for the coordinates
        const coords = feature.geometry.coordinates as Position[][];

        // compare the polygons
        try {
          const score = this.compareGeoJsonFeatures(coords, field.polygon.coordinates);
          if (score >= 85) {
            if (
              !_featuresToMatch.some(
                (el) => el.feature.properties.clientUniqueId === feature.properties.clientUniqueId
              )
            ) {
              _featuresToMatch.push({ field, feature });
            }
            matched = true;
            break;
          }
        } catch (e) {
          console.error('Error comparing polygons', e);
        }
      }

      if (!matched) {
        _featuresToImport.push(feature);
      }
    });

    // quick map from id to feature
    const featureMapExternalResId = new Map(res.features.map((f) => [f.properties.id, f]));

    const featureMapClientUniqueIds = new Map(
      res.features.map((f) => [f.properties.clientUniqueId, f])
    );

    // for each field, search for cultivations
    this.fields()?.forEach((field) => {
      const ageaResources = field.externalResources?.filter((r) => r.externalSource === 'AGEA');
      //  If the field is not coming from agea we can skip this step
      if (!ageaResources?.length) {
        return;
      }

      // Define a variable to hold the feature
      let feature;

      // first we check if the field has metadata
      if (field.metaData?.length) {
        // Looking for the client unique id in the field's metadata
        const clientUniqueIdMeta = field.metaData.find(
          (meta) => meta.key === 'clientUniqueId'
        )?.value;

        if (clientUniqueIdMeta) {
          // if there is a field with AGEA external resource no crop match to do
          // we can use the clientUniqueId from the metadata
          feature = featureMapClientUniqueIds.get(Number(clientUniqueIdMeta));
        }
      } else {
        // If there is no metadata, we can use the external resource id for legacy reasons
        feature = featureMapExternalResId.get(ageaResources[0].externalId);
      }

      // if there is no feature with the clientUniqueId, we can skip this field
      if (!feature) {
        return;
      }

      // else if there is a field with AGEA
      feature.properties.coltivazioni?.forEach((cult: HubfarmCultivation, index) => {
        if (!this.crops.length) {
          _cropsToMatch.push({
            parentId: feature.properties.id,
            id: feature.properties.clientUniqueId,
            field,
            crop: this.createCropFromCultivation(
              cult,
              field,
              index,
              feature.properties.clientUniqueId,
              feature.properties
            ),
          });
          return;
        }
        // if crop has metadata, we check for clientUniqueId
        let existingCrop: Crop;

        // Check if there is a crop with clientUniqueId
        this.crops.forEach((crop) => {
          // if crop has metadata, we check for clientUniqueId
          const clientUniqueIdMeta = crop.metaData?.find(
            (meta) => meta.key === 'clientUniqueId'
          )?.value;

          // if there is the value
          if (clientUniqueIdMeta) {
            // if crop has metadata, we check for clientUniqueId
            if (clientUniqueIdMeta === feature.properties.clientUniqueId.toString()) {
              existingCrop = crop;
            }
            return; // we can skip the rest of the checks
          }
          // if crop has no metadata, we check for external resources to keep in account legacy imported crops
          const externalResource = crop.externalResources?.find(
            (r) => r.externalSource === 'AGEA' && r.externalId === cult.idColt
          );
          // if there is a crop with the same external resource id, we can use it
          if (externalResource) {
            existingCrop = crop;
          }
        });

        // if crop does not exist, we create a new one
        if (!existingCrop) {
          _cropsToMatch.push({
            parentId: feature.properties.id,
            id: Number(feature.properties.clientUniqueId),
            field,
            crop: this.createCropFromCultivation(
              cult,
              field,
              index,
              feature.properties.clientUniqueId,
              feature.properties
            ),
          });
        }
        // reset for next iteration
        existingCrop = null;
      });
    });

    // check for crops coming from a multipolygon to split the area from the parent on all the crops equally
    // this is needed because the area is not the same for each polygon
    // and the area is used to calculate the crop size

    // emit for the UI, sorted by numeric id
    this.$plansToImport.next(
      _featuresToImport.sort(
        (a, b) => Number(a.properties.clientUniqueId) - Number(b.properties.clientUniqueId)
      )
    );
    this.$plansToMatch.next(
      _featuresToMatch.sort(
        (a, b) => Number(a.feature.properties.id) - Number(b.feature.properties.id)
      )
    );
    this.$cropsToMatch.next(_cropsToMatch.sort((a, b) => Number(a.id) - Number(b.id)));
  }

  /**
   * Compare two polygons (each given by an array of [longitude, latitude] coords)
   * to compute a similarity percentage based on their overlapping area.
   *
   * @param polygon1 - Coordinates for the first Polygon ring.
   *                   Example: [[0,0], [2,0], [2,2], [0,2], [0,0]]
   * @param polygon2 - Coordinates for the second Polygon ring.
   * @returns A number between 0 and 100 representing the overlap percentage.
   */
  compareGeoJsonFeatures(polygon1: Position[][], polygon2: Position[][]): number {
    // Create two Polygon features.
    // In GeoJSON, a Polygon's coordinates must be an array of linear rings,
    // so we wrap each provided ring in an extra array.
    const polyFeature1 = turf.polygon(polygon1);
    const polyFeature2 = turf.polygon(polygon2);

    // Create a FeatureCollection from these two polygon features.
    // Note: turf.featureCollection expects an array of features.
    const fc = turf.featureCollection([polyFeature1, polyFeature2]);

    // Call turf.intersect with the FeatureCollection.

    const intersection = turf.intersect(fc);

    // If there's no intersection, return 0.
    if (!intersection) {
      return 0;
    }

    // Calculate areas.
    const intersectionArea = turf.area(intersection);
    const area1 = turf.area(polyFeature1);
    const area2 = turf.area(polyFeature2);

    // Compute the union area.
    const unionArea = area1 + area2 - intersectionArea;
    if (unionArea === 0) {
      return 100;
    }

    // Return the overlap as a percentage.
    return (intersectionArea / unionArea) * 100;
  }

  getColumnConfigByType(type: string) {
    if (type == 'dossier') {
      return DOSSIER_COLUMN_CONFIG;
    }
    if (type == 'equipments') {
      return EQUIPMENT_COLUMN_CONFIG;
    }
    if (type == 'particelle') {
      return PARTICELLA_COLUMN_CONFIG;
    }
    if (type == 'owners') {
      return OWNERS_COLUMN_CONFIG;
    }
    if (type == 'territory') {
      return TERRITORY_COLUMN_CONFIG;
    }
    if (type == 'zootechnic') {
      return ZOOTECHNIC_COLUMN_CONFIG;
    }
    if (type == 'building') {
      return FABBRICATO_COLUMN_CONFIG;
    }
    return [];
  }

  createCropFromCultivation(
    cultivation: HubfarmCultivation,
    field: Field,
    index: number = null,
    clientUniqueId: number,
    featureProperties?: HubfarmProperties
  ): Crop {
    // getting current farm
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));

    const crop = new Crop();
    crop.externalResources = [
      {
        externalId: cultivation.idColt,
        externalSource: 'AGEA',
        // Generating unique name for the crop to avoid
        // future issues when matching crops
        externalName: `${cultivation.idColt}-${index}`,
        defaultResource: null,
        externalSerialNumber: null,
      },
    ];

    const matchedData = this.matchHubfarmCultivationData(cultivation);

    crop.commodity = matchedData?.commodity;
    crop.variety = matchedData?.variety;
    crop.field = field;

    crop.companyId = companySelected.companyId;

    // Setting the size in ha
    crop.size = cultivation.supeColt / 10000;

    // Setting the cropyear
    crop.year = moment(cultivation.dataFineColt, 'DD/MM/YYYY').get('year');

    // set multiYear, check if there are at least 3 year between datafineColt and dataInizioColt
    const dataInizioColt = moment(cultivation.dataInizColt, 'DD/MM/YYYY');
    const dataFineColt = moment(cultivation.dataFineColt, 'DD/MM/YYYY');
    crop.multiYear = dataFineColt.diff(dataInizioColt, 'years') >= 3;

    crop.useId = matchedData.commonIntendedUse?.id;
    crop.intendedUseId = matchedData.commonIntendedUse?.id;
    crop.qualityId = matchedData.commonQuality?.id;
    crop.useId = matchedData.commonUse?.id;

    // Adding a plant if crop is multiYear
    if (crop.multiYear) {
      // A multiYearCrop requires a plant
      crop.plant = new Plant();
      crop.plant.companyId = companySelected.companyId;
      crop.plant.plantsPlantingDate = dataInizioColt.toDate();
      crop.plant.plantsHarvestingDate = dataFineColt.year() === 9999 ? null : dataFineColt.toDate();
      crop.plant.externalResources = [
        {
          externalId: cultivation.identificativoPianoColtivazione,
          externalSource: 'AGEA',
          externalName: null,
          defaultResource: null,
          externalSerialNumber: null,
        },
      ];
    }

    // Adding metadata to crop
    const metaData: TenantMetaData[] = [];

    // Add clientUniqueId
    metaData.push({
      key: 'clientUniqueId',
      value: clientUniqueId?.toString() ?? '',
    });

    // Add fields from featureProperties
    metaData.push({
      key: 'idAppe',
      value:
        featureProperties?.idAppe?.toString() ??
        featureProperties?.identificativoAppezzamento?.toString() ??
        '',
    });
    metaData.push({
      key: 'idPcgVers',
      value:
        featureProperties?.idPcgVers?.toString() ??
        featureProperties?.identificativoPianoColtivazione?.toString() ??
        '',
    });
    metaData.push({
      key: 'idIsol',
      value:
        featureProperties?.idIsol?.toString() ??
        featureProperties?.identificativoIsola?.toString() ??
        '',
    });
    metaData.push({
      key: 'featureLabel',
      value: featureProperties?.featureLabel?.toString() ?? featureProperties?.id?.toString() ?? '',
    });
    metaData.push({
      key: 'cuaa',
      value: featureProperties?.cuaa?.toString() ?? '',
    });

    // Add fields from cultivation
    metaData.push({ key: 'idColt', value: cultivation.idColt?.toString() ?? '' });
    metaData.push({ key: 'codiOccu', value: cultivation.codiOccu?.toString() ?? '' });
    metaData.push({ key: 'codiDestUsoo', value: cultivation.codiDestUsoo?.toString() ?? '' });
    metaData.push({ key: 'codiUsoo', value: cultivation.codiUsoo?.toString() ?? '' });
    metaData.push({ key: 'codiQual', value: cultivation.codiQual?.toString() ?? '' });
    metaData.push({ key: 'codiOccuVari', value: cultivation.codiOccuVari?.toString() ?? '' });
    metaData.push({ key: 'dataInizColt', value: cultivation.dataInizColt?.toString() ?? '' });
    metaData.push({ key: 'dataFineColt', value: cultivation.dataFineColt?.toString() ?? '' });
    metaData.push({ key: 'supeColt', value: cultivation.supeColt?.toString() ?? '' });

    // Assign to crop
    crop.metaData = metaData;

    return crop;
  }

  createFieldFromHubfarmFeature(feature: HubfarmFeature) {
    // getting current farm
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));

    const featureCopy: HubfarmFeature = JSON.parse(JSON.stringify(feature));
    const newField = new Field();

    newField.name = feature.properties.id;
    newField.farm = companySelected.companyId;
    newField.companyId = companySelected.companyId;
    newField.polygon = featureCopy.geometry.bbox;
    delete featureCopy.properties;

    const geoJson = {
      type: 'FeatureCollection',
      features: [featureCopy],
    };
    newField.geoJsonArea = JSON.stringify(geoJson);
    newField.size = turf.area(featureCopy) / 10000;
    newField.metaData = [] as TenantMetaData[];
    const metaDataArr = newField.metaData as TenantMetaData[];
    metaDataArr.push({
      key: 'identificativoPianoColtivazione',
      value:
        feature.properties.identificativoPianoColtivazione?.toString() ??
        feature.properties.idPcgVers?.toString() ??
        '',
    });

    metaDataArr.push({
      key: 'codiBarrScheVali',
      value: feature.properties.codiBarrScheVali?.toString() ?? '',
    });

    metaDataArr.push({
      key: 'identificativoAppezzamento',
      value:
        feature.properties.identificativoAppezzamento?.toString() ??
        feature.properties.idAppe?.toString() ??
        '',
    });

    metaDataArr.push({
      key: 'identificativoIsola',
      value:
        feature.properties.identificativoIsola?.toString() ??
        feature.properties.idIsol?.toString() ??
        '',
    });

    metaDataArr.push({
      key: 'clientUniqueId',
      value: feature.properties.clientUniqueId?.toString() ?? '',
    });

    const extResource: ExternalResource = {
      externalId:
        feature.properties.id?.toString() ?? feature.properties.featureLabel?.toString() ?? '',
      externalSource: 'AGEA',
      externalName:
        feature.properties.id?.toString() ?? feature.properties.featureLabel?.toString() ?? '',
      defaultResource: null,
      externalSerialNumber: null,
    };

    newField['externalResources'] = [extResource];
    return newField;
  }

  /**
   * Match the hubfarm cultivation with the xfarm entities
   * @param cultivation
   * @returns {
   *   commonCommodity: CommonCommodity,
   *   commodity: Commodity,
   *   commonVariety: CommonVariety,
   *   variety: Variety,
   *   commonIntendedUse: CommonIntendedUse,
   *   commonQuality: CommonQuality,
   *   commonUse: CommonUse
   * }
   *
   **/
  matchHubfarmCultivationData(cultivation: HubfarmCultivation): MatchedHubfarmCultivationData {
    try {
      // Helper function to find a common item by externalId and source
      const findCommonByExternalId = (collection, externalId) =>
        collection.find((item) =>
          item.externalResources?.some(
            (res) => res.externalId === externalId && res.externalSource === 'AGEA'
          )
        );

      // Extract common references
      const commonCommodity = findCommonByExternalId(this.commonCommodities, cultivation.codiOccu);
      const commonVariety = findCommonByExternalId(this.commonVarieties, cultivation.codiOccuVari);
      const commonIntendedUse = findCommonByExternalId(
        this.commonIntendedUses,
        cultivation.codiDestUtil
      );
      const commonQuality = findCommonByExternalId(this.commonQualities, cultivation.codiQual);
      const commonUse = findCommonByExternalId(this.commonUses, cultivation.codiUsoo);

      // Get commodity and variety from xFarm using the AGEA externalId
      const commodity = this.commodities.find((comm) => {
        const externalId =
          commonCommodity?.externalResources &&
          this.getExternalSource(commonCommodity.externalResources, 'XFARM')
            ? this.getExternalSource(commonCommodity.externalResources, 'XFARM').externalId
            : undefined;
        return comm.id === parseInt(externalId);
      });

      const variety = this.varieties.find((variety) => {
        const externalId =
          commonVariety?.externalResources &&
          this.getExternalSource(commonVariety.externalResources, 'XFARM')
            ? this.getExternalSource(commonVariety.externalResources, 'XFARM').externalId
            : undefined;
        return variety.id === parseInt(externalId);
      });

      return {
        commonCommodity,
        commodity,
        commonVariety,
        variety,
        commonIntendedUse,
        commonQuality,
        commonUse,
        cultivation,
      };
    } catch (error) {
      console.error('Error matching Hubfarm cultivation data:', error);
      return null;
    }
  }

  /**
   * Get an external source from the externalResources array
   * @param externalResources
   * @param externalSourceName
   * @param externalId
   * @returns ExternalResource
   */
  getExternalSource(
    externalResources: ExternalResource[],
    externalSourceName: string,
    externalId?: string
  ): ExternalResource {
    try {
      const externalSource = externalResources.find(
        (extRes) =>
          extRes.externalSource &&
          extRes.externalSource === externalSourceName &&
          (externalId ? extRes.externalId === externalId : true)
      );
      return externalSource;
    } catch (error) {
      console.error('Error getting external source:', error);
      return null;
    }
  }

  /**
   * Filters an array of HubfarmFeatures removing those that are not valid polygons or fall outside of the world on google maps
   * @param features - Array of HubfarmFeature objects
   */
  filterValidFeatures(features: HubfarmFeature[]): HubfarmFeature[] {
    return features.filter((feature) => {
      // Check if the feature has valid coordinates
      if (!feature.geometry || !feature.geometry.coordinates) {
        return false;
      }

      // Accept only polygon and multiPolygon
      if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
        return false;
      }

      const isMultiPolygon = feature.geometry.type === 'MultiPolygon';
      const polygons = isMultiPolygon
        ? feature.geometry.coordinates
        : [feature.geometry.coordinates];

      // Check if all coordinates are within valid range for Google Maps
      const isValid = polygons.every((polygon) => {
        return polygon.every((ring: Position[]) => {
          return ring.every(([lng, lat]) => lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90);
        });
      });

      // Check if all polygons are closed
      const isClosed = polygons.every((polygon) => {
        return (
          polygon[0][0][0] === polygon[0][polygon[0].length - 1][0] &&
          polygon[0][0][1] === polygon[0][polygon[0].length - 1][1]
        );
      });

      // if not valid, log invalid features
      if (!isValid || !isClosed) {
        // log the feature and the erro reason
        console.warn('Invalid feature filtered out:', feature, {
          isValid,
          isClosed,
        });
      }

      return isValid && isClosed;
    });
  }

  resetSelectedItems() {
    this.$selectedPlansToImport.next([]);
    this.$selectedPlansToMatch.next([]);
    this.$selectedCropsToMatch.next([]);
    this.selectedFeatures.set([]);
    this.selectedFieldMatches.set([]);
    this.selectedCropMatches.set([]);
  }

  updateSelectedFeaturesToImport(items: HubfarmFeature[]) {
    this.$selectedPlansToImport.next(items);
    this.selectedFeatures.set(items);
  }

  updateSelectedFeaturesToMatch(items: HubfarmMatchFieldFeature[]) {
    this.$selectedPlansToMatch.next(items);
    this.selectedFieldMatches.set(items);
  }

  updateSelectedCropsToMatch(items: HubfarmMatchFieldCrops[]) {
    this.$selectedCropsToMatch.next(items);
    this.selectedCropMatches.set(items);
  }

  // This method checks if the field and crop lists have changed since the last sync
  public syncIfChangedOnComponentEnter(): boolean {
    const currentFields = this.fieldService.fieldList$.getValue();
    const currentCrops = this.cropService.cropList$.getValue();

    const fieldsChanged = !isEqual(currentFields, this.lastSyncedFields);
    const cropsChanged = !isEqual(currentCrops, this.lastSyncedCrops);

    if (fieldsChanged || cropsChanged) {
      // Salva lo snapshot aggiornato
      this.lastSyncedFields = currentFields;
      this.lastSyncedCrops = currentCrops;

      return true;
    } else {
      return false;
    }
  }
}
